import React, { Fragment, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import HeaderV2 from "../../components/headerV2/HeaderV2";
import FooterV2 from "../../components/footerV2/FooterV2";
import AboutHero from "../../components/aboutHero/AboutHero";
import useScrollReveal from "../../hooks/useScrollReveal";
import Services from "../../api/service";

import AboutUs from "../../images/donate/about-us-v2.jpg";
import DonateInKind from "../../images/donate/donation-1.webp";
import CorporateDonation from "../../images/donate/coporate-donation.webp";
import MainCta from "../../images/donate/main-cta.webp";
import LifeSaverArmy from "../../images/home/lfe-saver-logo.webp";

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
// AboutHero + .about-head heading styles live in aboutV2.css; import it so the
// shared hero band and centered headings render even if no About page loaded
// first. Its `.about-live`-scoped rules never touch this `.donate-live` page.
import "../AboutUsPage/aboutV2.css";
import "./donateV2.css";

/* Small arrow used inside the pill buttons (matches the homeV2 .btn svg). */
const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Why-donate cards + donation tiers are pulled from the shared services
   data so the copy stays in sync with the rest of the site (identical to
   the previous page's Services.slice(49,52) and Services.slice(52,57)). */
const whyDonate = Services.slice(49, 52);
const donationTiers = Services.slice(52, 57);

/* Bank transfer details. */
const bankDetails = [
  { k: "Name", v: "Jainam Jivika Foundation" },
  { k: "Account No.", v: "023100100000097" },
  { k: "Bank Name", v: "The Nasik Merchants Co-operative Bank Ltd., Nashik" },
  { k: "Branch", v: "Malegaon" },
  { k: "Account Type", v: "Current Account" },
  { k: "IFSC", v: "NMCB0000024" },
];

const Donate = ({ path }) => {
  const ClickHandler = () => window.scrollTo(10, 0);

  const rootRef = useRef(null);
  useScrollReveal(rootRef);

  // Setting metadata and schemadata
  const [metadata, setMetadata] = useState({ title: "JJFIndia | Donate" });
  const [schemadata, setSchemadata] = useState({});

  useEffect(() => {
    let tempMetadata = metadataJSON[path];
    let tempSchemadata = schemadataJSON[path];

    if (tempMetadata) {
      setMetadata(tempMetadata);
    } else {
      tempMetadata = {
        title: "JJFIndia | Donate",
        description: "Donate to Jainam Jivika Foundation and help Life Saver Army organise blood donation camps, grow the donor network and support patients in need.",
        keywords: "",
        robots: "index, follow",
        canonical: "https://jjfindia.com/join-us/donate",
        openGraph: {
          title: "JJFIndia | Donate",
          description: "Donate to Jainam Jivika Foundation and help Life Saver Army organise blood donation camps, grow the donor network and support patients in need.",
          url: "https://jjfindia.com/join-us/donate",
        },
        twitter: {
          card: "summary_large_image",
          title: "JJFIndia | Donate",
          description: "Donate to Jainam Jivika Foundation and help Life Saver Army organise blood donation camps, grow the donor network and support patients in need.",
        },
      };
      setMetadata(tempMetadata);
    }

    if (tempSchemadata) setSchemadata(tempSchemadata);
  }, [path]);

  return (
    <Fragment>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:title" content={metadata.openGraph?.title} />
        <meta property="og:description" content={metadata.openGraph?.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta name="twitter:title" content={metadata.twitter?.title} />
        <meta name="twitter:description" content={metadata.twitter?.description} />
        <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
      </Helmet>

      <div className="jjfv2 donate-live" ref={rootRef}>
        <HeaderV2 />

        <main className="page_content">
          {/* ---------- Page hero ---------- */}
          <AboutHero
            eyebrow="Join Us"
            title="Donate"
            crumbs={[{ label: "Home", to: "/" }, { label: "Join Us" }, { label: "Donate" }]}
          />

          {/* ---------- Intro: your contribution can change lives ---------- */}
          <section className="section donate-intro">
            <div className="wrap">
              <div className="split">
                <div className="donate-media reveal">
                  <div className="ph">
                    <img src={AboutUs} alt="Volunteer giving food and support to children in need." loading="lazy" />
                  </div>
                </div>
                <div className="reveal">
                  <span className="eyebrow">Donate to Jainam Jivika Foundation</span>
                  <h2 style={{ marginBottom: "0.6rem" }}>Together, We Can Save More Lives</h2>
                  <p className="lead" style={{ marginTop: 0 }}>
                    At Jainam Jivika Foundation, we believe that timely blood can mean the difference between life and loss. Your contribution helps strengthen the Life Saver Army by supporting blood donation drives, donor awareness programmes, emergency response efforts and volunteer-led initiatives. Every contribution enables us to reach more donors, support more patients and build a stronger network of lifesavers across communities.
                  </p>
                  <div className="about-cta">
                    <Link to="/contact-us" onClick={ClickHandler} className="btn btn--amber">
                      Get in Touch <Arrow />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- Why donate ---------- */}
          <section className="section section--mist">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Every Contribution Creates Hope</span>
                <h2>Why Donate to Jainam Jivika Foundation?</h2>
              </div>
              <div className="ways ways--3">
                {whyDonate.map((item, index) => (
                  <article className={"way reveal" + (index ? " d" + index : "")} key={index}>
                    <div className="wic">
                      <img src={item.icon} alt={item.alt || item.title} loading="lazy" />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- How your donation helps (tiers) ---------- */}
          <section className="section">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Every Drop Matters, Every Donation Saves Lives</span>
                <h2>How Your Donation Helps?</h2>
              </div>
              <div className="tiers">
                {donationTiers.map((tier, index) => (
                  <article className={"tier reveal" + (index ? " d" + Math.min(index, 3) : "")} key={index}>
                    <div className="tier__ico">
                      <img src={tier.icon} alt={tier.alt || tier.title} loading="lazy" />
                    </div>
                    <div className="tier__amt">{tier.title}</div>
                    <p>{tier.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- Ways to donate ---------- */}
          <section className="section section--mist">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Choose Your Way to Make a Difference</span>
                <h2>Ways to Donate</h2>
              </div>
              <div className="give">
                {/* Bank transfer */}
                <div className="give-bank reveal">
                  <h3>Bank Transfer</h3>
                  <p className="give-bank__sub">Transfer your contribution directly to our official bank account.</p>
                  <ul className="give-bank__list">
                    {bankDetails.map((row, index) => (
                      <li key={index}>
                        <span className="k">{row.k}</span>
                        <span className="v">{row.v}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* In-kind + corporate */}
                <div className="give-cards">
                  <article className="give-card reveal">
                    <div className="give-card__ico">
                      <img src={DonateInKind} alt="Hand placing money in donation box icon" loading="lazy" />
                    </div>
                    <h3>Donate in Kind</h3>
                    <p>
                      Support our blood donation initiatives by contributing refreshments, event materials, awareness resources or other essentials required for organising donation camps.
                    </p>
                    <Link to="/contact-us" onClick={ClickHandler} className="btn btn--amber">
                      Contact Us <Arrow />
                    </Link>
                  </article>

                  <article className="give-card reveal d1">
                    <div className="give-card__ico">
                      <img src={CorporateDonation} alt="Hands holding heart for charity icon." loading="lazy" />
                    </div>
                    <h3>Corporate Giving</h3>
                    <p>
                      Partner with Jainam Jivika Foundation through your Corporate Social Responsibility (CSR) initiatives to organise blood donation drives and create a lasting community impact.
                    </p>
                    <Link to="/corporate-donations" onClick={ClickHandler} className="btn btn--amber">
                      Learn More <Arrow />
                    </Link>
                  </article>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- Key initiative: Life Saver Army flagship ---------- */}
          <section className="section">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Saving Lives Through Every Contribution</span>
                <h2>Our Flagship Initiative</h2>
                <p className="lead">Your support strengthens:</p>
              </div>

              <div className="lsa2 reveal">
                {/* LEFT — dark brand panel */}
                <div className="lsa2__brand">
                  <div className="lsa2__drop" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2S5 10 5 15a7 7 0 0 0 14 0c0-5-7-13-7-13z" />
                    </svg>
                  </div>
                  <div className="lsa2__logo">
                    <img src={LifeSaverArmy} alt="Life Saver Army logo" loading="lazy" />
                  </div>
                  <span className="lsa2__tag">
                    <i className="fa-solid fa-droplet"></i> Our Flagship Initiative
                  </span>
                  <h3 className="lsa2__title">Life Saver Army</h3>
                  <p className="lsa2__tagline">
                    No patient should ever lose their life for want of blood.
                  </p>
                  <div className="lsa2__stats">
                    <div className="lsa2__stat">
                      <div className="n">5.6K<b>+</b></div>
                      <div className="l">Total Registrations</div>
                    </div>
                    <div className="lsa2__stat">
                      <div className="n">9</div>
                      <div className="l">Indian States</div>
                    </div>
                    <div className="lsa2__stat">
                      <div className="n">24/7</div>
                      <div className="l">Emergency Response</div>
                    </div>
                  </div>
                </div>

                {/* RIGHT — light content panel */}
                <div className="lsa2__panel">
                  <p className="lsa2__desc">
                    A nationwide initiative dedicated to promoting voluntary blood donation, connecting donors with patients in need and inspiring communities to save lives through the gift of blood.
                  </p>
                  <ul className="lsa2__feat">
                    <li>
                      <span className="ic"><i className="fa-solid fa-droplet"></i></span>
                      <div>
                        <b>Emergency Blood, On Demand</b>
                        <p>Connects patients with willing voluntary donors nearby the moment a need arises.</p>
                      </div>
                    </li>
                    <li>
                      <span className="ic"><i className="fa-solid fa-users"></i></span>
                      <div>
                        <b>A Growing Donor Community</b>
                        <p>Thousands of registered lifesavers, ready to step up whenever they're called.</p>
                      </div>
                    </li>
                    <li>
                      <span className="ic"><i className="fa-solid fa-location-dot"></i></span>
                      <div>
                        <b>Right Where It's Needed</b>
                        <p>Coordinated with hospitals and blood banks across nine Indian states.</p>
                      </div>
                    </li>
                  </ul>
                  <div className="lsa2__cta">
                    <Link to="/initiatives/life-saver-army" onClick={ClickHandler} className="btn btn--green">
                      Explore Life Saver Army <Arrow />
                    </Link>
                    <Link to="/join-us/volunteer-opportunities" onClick={ClickHandler} className="btn btn--amber">
                      Become a Life Saver <Arrow />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- Your impact matters (closing CTA) ---------- */}
          <section className="section section--mist">
            <div className="wrap">
              <div className="split">
                <div className="donate-media reveal">
                  <div className="ph">
                    <img src={MainCta} alt="Donors providing meals and essentials to children." loading="lazy" />
                  </div>
                </div>
                <div className="reveal">
                  <span className="eyebrow">Your Support, Their Future</span>
                  <h2>Your Support Makes Every Drop Count</h2>
                  <p className="lead">
                    Every donation, regardless of its size, helps us inspire more blood donors, organise more donation camps and respond to urgent blood requirements. Together, we can build a future where no life is lost due to the unavailability of blood. Donate today. Save a life tomorrow.
                  </p>
                  <div className="about-cta">
                    <Link to="/contact-us" onClick={ClickHandler} className="btn btn--amber">
                      Contact Us for More Information <Arrow />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <FooterV2 />
      </div>
    </Fragment>
  );
};

export default Donate;
